import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  InputRef,
  Modal,
  Select,
  Space,
  Typography,
  Table,
  TableProps,
  message
} from 'antd'
import React from 'react'
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  SwapOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import qs from 'qs'
import { MenuInfo } from 'rc-menu/lib/interface'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import debounce from 'lodash.debounce'
import { ColumnsType } from 'antd/es/table'
import { sellerApi } from 'api/seller'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { SellersSummary } from 'components/sellers/SellersSummary/SellersSummary'
import { SellersReportModal } from 'components/sellers/SellersReportModal/SellersReportModal'
import {
  openStatusHint,
  SELLER_DISABLE_REASONS,
  SellerDisableStatus
} from 'components/sellers/SellerDisableStatus/SellerDisableStatus'
import {
  ESellerDisableReason,
  ESellerStatus,
  IAdminSellersSummary,
  ISeller,
  ISellersQueryFilters
} from 'models/seller'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { superAdminRoutes } from 'shared/constants/routes'
import { showServerError } from 'shared/utils/showServerError'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { highlightText, highlightSearchText } from 'shared/utils/highlightSearchText'
import { getSellerStatusItems, sellerStatuses } from 'shared/constants/sellers'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { cleanObject } from 'shared/utils/cleanObject'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import styles from './List.module.scss'

// Отключен — любая причина; выбор отдельных причин сужает его
const disableStateFilters = [
  { text: 'Активный', value: 'ACTIVE' },
  { text: 'Неактивный', value: 'INACTIVE' },
  {
    text: 'Отключен',
    value: 'DISABLED',
    children: Object.values(ESellerDisableReason).map((reason) => ({
      text: SELLER_DISABLE_REASONS[reason].label,
      value: reason
    }))
  }
]

const statusFirstOptions = [
  { label: 'Сначала активные', value: 'ACTIVE' },
  { label: 'Сначала неактивные', value: 'INACTIVE' },
  { label: 'Сначала отключённые', value: 'DISABLED' }
]
const DEFAULT_STATUS_FIRST = 'ACTIVE'

// Таблица модерации без фильтров основной: показывает всех ожидающих
const withoutFilters = (columns: ColumnsType<ISeller>): ColumnsType<ISeller> =>
  columns.map((column) => ({
    ...column,
    filters: undefined,
    filteredValue: undefined,
    filterDropdown: undefined,
    filterIcon: undefined,
    onFilterDropdownOpenChange: undefined
  }))

export const SellersList: React.FC = () => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const { isPhone, isPhoneLandscape } = useBreakpoint()
  const isSortButton = isPhone || isPhoneLandscape

  const [isFirstRequestFinished, setIsFirstRequestFinished] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [sellers, setSellers] = React.useState<ISeller[]>([])
  const [isReportModalOpen, setIsReportModalOpen] = React.useState(false)

  const [selectedItemsId, setSelectedItemsId] = React.useState<string[]>([])

  const [globalSearch, setGlobalSearch] = React.useState('')

  const [idSearch, setIdSearch] = React.useState('')
  const idSearchRef = React.useRef<InputRef>(null)

  const [fullNameSearch, setFullNameSearch] = React.useState('')
  const fullNameSearchRef = React.useRef<InputRef>(null)

  const [shopNameSearch, setShopNameSearch] = React.useState('')
  const shopNameSearchRef = React.useRef<InputRef>(null)

  const [phoneSearch, setPhoneSearch] = React.useState('')
  const phoneSearchRef = React.useRef<InputRef>(null)

  const [disableStates, setDisableStates] = React.useState<string[]>([])
  const [statusFirst, setStatusFirst] = React.useState(DEFAULT_STATUS_FIRST)

  // Список показывает либо подтверждённых, либо отклонённых продавцов
  const [status, setStatus] = React.useState<ESellerStatus>(ESellerStatus.APPROVED)

  const [summary, setSummary] = React.useState<IAdminSellersSummary | null>(null)
  const [isSummaryFailed, setIsSummaryFailed] = React.useState(false)

  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const pageSize = 10

  const [moderationSellers, setModerationSellers] = React.useState<ISeller[]>([])
  const [moderationTotal, setModerationTotal] = React.useState(0)
  const [moderationPage, setModerationPage] = React.useState(1)
  const [isModerationLoading, setIsModerationLoading] = React.useState(false)

  const showDeleteConfirm = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Вы действительно хотите удалить выбранных продавцов?',
      content: 'Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return new Promise((resolve) => {
          setProductsAction().then(resolve)
        })
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const getSummary = async () => {
    try {
      const response = await sellerApi.getSellersSummary()
      setSummary(response.data)
      setIsSummaryFailed(false)
    } catch (e) {
      setIsSummaryFailed(true)
      showServerError(e)
    }
  }

  const getSellers = async (skip: number, status: ESellerStatus, data?: ISellersQueryFilters) => {
    const nextStatusFirst = data?.status_first ?? statusFirst
    try {
      setIsLoading(true)
      const response = await sellerApi.getSellers({
        skip: String(skip),
        limit: String(pageSize),
        all_fields: data?.all_fields ?? globalSearch,
        number: data?.number ?? idSearch,
        full_name: data?.full_name ?? fullNameSearch,
        shop_name: data?.shop_name ?? shopNameSearch,
        tel: data?.tel ?? phoneSearch,
        disable_state: data?.disable_state ?? disableStates.join(','),
        status_first: nextStatusFirst,
        status
      })
      setTotal(response.data.count)
      setSellers(response.data.sellers)
      setSearchParams(cleanObject({
        page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
        all_fields: data?.all_fields ?? globalSearch,
        number: data?.number ?? idSearch,
        full_name: data?.full_name ?? fullNameSearch,
        shop_name: data?.shop_name ?? shopNameSearch,
        tel: data?.tel ?? phoneSearch,
        disable_state: data?.disable_state ?? disableStates.join(','),
        status_first: nextStatusFirst === DEFAULT_STATUS_FIRST ? '' : nextStatusFirst,
        status: status === ESellerStatus.DECLINED ? status : ''
      }))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const getModerationSellers = async (page: number) => {
    try {
      setIsModerationLoading(true)
      const response = await sellerApi.getSellers({
        skip: String((page - 1) * pageSize),
        limit: String(pageSize),
        status: ESellerStatus.WAITING_APPROVE
      })
      setModerationTotal(response.data.count)
      setModerationSellers(response.data.sellers)
      setModerationPage(page)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsModerationLoading(false)
    }
  }

  const setProductsAction = async () => {
    try {
      const response = await sellerApi.setActions({
        toDelete: selectedItemsId.join(',')
      })
      message.success(response.data.message)
      if (selectedItemsId.length === sellers.length) {
        getSellers(0, status)
        setCurrentPage(1)
      } else {
        getSellers((currentPage - 1) * pageSize, status)
      }
      getSummary()
      setSelectedItemsId([])
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const onChangeSellerStatus = async (e: MenuInfo, sellerId: string) => {
    try {
      setIsLoading(true)
      await sellerApi.changeSellerStatus(sellerId, { status: e.key as ESellerStatus.APPROVED | ESellerStatus.DECLINED })
      // Со сменой статуса продавец уходит из своей таблицы — перечитываем обе
      const isModeration = moderationSellers.some(({ _id }) => _id === sellerId)
      const page = !isModeration && sellers.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage
      setCurrentPage(page)
      getSellers((page - 1) * pageSize, status)
      getModerationSellers(
        isModeration && moderationSellers.length === 1 && moderationPage > 1
          ? moderationPage - 1
          : moderationPage
      )
      getSummary()
    } catch (e) {
      showServerError(e)
      setIsLoading(false)
    }
  }

  const saveFieldValue = (data: ISellersQueryFilters) => {
    getSellers(0, status, data)
    setCurrentPage(1)
  }

  const onChangeGlobalSearch = (searchValue: string) => {
    getSellers(0, status, { all_fields: searchValue })
    setCurrentPage(1)
  }

  const onChangeDeclinedOnly = (isDeclinedOnly: boolean) => {
    const newStatus = isDeclinedOnly ? ESellerStatus.DECLINED : ESellerStatus.APPROVED
    setStatus(newStatus)
    // Отмеченные в другом режиме строки не видны — удалять их вслепую нельзя
    setSelectedItemsId([])
    // У отклонённых нет отключения: фильтр по нему пустил бы пустой список
    setDisableStates([])
    getSellers(0, newStatus, { disable_state: '' })
    setCurrentPage(1)
  }

  const onChangeStatusFirst = (value: string) => {
    setStatusFirst(value)
    getSellers(0, status, { status_first: value })
    setCurrentPage(1)
  }

  const onRowSelection = (selectedItemsId: React.Key[]) => {
    setSelectedItemsId(selectedItemsId as string[])
  }

  // Страницы листаются здесь, а не в pagination.onChange: при смене фильтра antd
  // зовёт и его со страницей 1, и запрос со старым фильтром перетёр бы ответ
  const onTableChange: TableProps<ISeller>['onChange'] = ({ current = 1 }, filters, __, { action }) => {
    if (action === 'paginate') {
      getSellers((current - 1) * pageSize, status)
      setCurrentPage(current)
    }
    if (action !== 'filter') return
    const nextStates = (filters.status ?? []) as string[]
    setDisableStates(nextStates)
    getSellers(0, status, { disable_state: nextStates.join(',') })
    setCurrentPage(1)
  }

  const debouncedGetSellersByGlobalSearch = React.useCallback(debounce(onChangeGlobalSearch, DEBOUNCE_DELAY), [idSearch, fullNameSearch, shopNameSearch, phoneSearch, status, disableStates, statusFirst])

  React.useEffect(() => {
    if (window.location.search) {
      const params = qs.parse(window.location.search.substring(1))
      if (params.page) {
        setCurrentPage(Number(params.page))
      }
      if (params.all_fields) {
        setGlobalSearch(params.all_fields as string)
      }
      if (params.number) {
        setIdSearch((params.number as string).replace(/[^0-9]/g, ''))
      }
      if (params.full_name) {
        setFullNameSearch(params.full_name as string)
      }
      if (params.shop_name) {
        setShopNameSearch(params.shop_name as string)
      }
      if (params.tel) {
        setPhoneSearch((params.tel as string).replace(/[^0-9]/g, ''))
      }
      if (params.status === ESellerStatus.DECLINED) {
        setStatus(ESellerStatus.DECLINED)
      }
      if (params.disable_state) {
        setDisableStates((params.disable_state as string).split(','))
      }
      if (statusFirstOptions.some(({ value }) => value === params.status_first)) {
        setStatusFirst(params.status_first as string)
      }
    }
    setIsFirstRequestFinished(true)
    getSummary()
    getModerationSellers(1)
  }, [])

  React.useEffect(() => {
    if (isFirstRequestFinished) {
      getSellers((currentPage - 1) * pageSize, status)
      setCurrentPage(currentPage)
    }
  }, [isFirstRequestFinished])

  const columns: ColumnsType<ISeller> = React.useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'number',
        width: '8%',
        onCell: ({ _id }) => {
          return {
            onClick: () => navigate(getSellerEditPath(_id))
          }
        },
        render: (_, { number }) => {
          return (
            <Typography.Text style={{ wordWrap: 'break-word' }}>
              {highlightText(String(number ?? ''), globalSearch || idSearch)}
            </Typography.Text>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={idSearchRef}
                value={idSearch}
                onChange={(e) => setIdSearch(e.target.value.replace(/[^0-9]/g, ''))}
                onPressEnter={() => {
                  saveFieldValue({ number: idSearch })
                  close()
                }}
                placeholder='ID Продавца'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue({ number: idSearch })
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setIdSearch('')
                    saveFieldValue({ number: '' })
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => idSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: idSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: 'ФИО продавца',
        dataIndex: 'name',
        width: '21%',
        onCell: ({ _id }) => {
          return {
            onClick: () => navigate(getSellerEditPath(_id))
          }
        },
        render: (_, { user_id }) => {
          return (
            <Typography.Text>
              {highlightText(user_id?.full_name ?? '', globalSearch || fullNameSearch)}
            </Typography.Text>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={fullNameSearchRef}
                value={fullNameSearch}
                onChange={(e) => setFullNameSearch(e.target.value)}
                onPressEnter={() => {
                  saveFieldValue({ full_name: fullNameSearch })
                  close()
                }}
                placeholder='ФИО продавца'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue({ full_name: fullNameSearch })
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setFullNameSearch('')
                    saveFieldValue({ full_name: '' })
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => fullNameSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: fullNameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: 'Название магазина',
        dataIndex: 'shopName',
        width: '21%',
        onCell: ({ _id }) => {
          return {
            onClick: () => navigate(getSellerEditPath(_id))
          }
        },
        render: (_, { shop_details }) => {
          return (
            <Typography.Text>
              {highlightText(shop_details?.shop_name ?? '', globalSearch || shopNameSearch)}
            </Typography.Text>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={shopNameSearchRef}
                value={shopNameSearch}
                onChange={(e) => setShopNameSearch(e.target.value)}
                onPressEnter={() => {
                  saveFieldValue({ shop_name: shopNameSearch })
                  close()
                }}
                placeholder='Название магазина'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue({ shop_name: shopNameSearch })
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setShopNameSearch('')
                    saveFieldValue({ shop_name: '' })
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => shopNameSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: shopNameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: 'Номер телефона',
        dataIndex: 'tel',
        width: '20%',
        onCell: ({ _id }) => {
          return {
            onClick: () => navigate(getSellerEditPath(_id))
          }
        },
        render: (_, { user_id }) => {
          return (
            <Typography.Text>
              {!!user_id?.tel && highlightSearchText(phoneMaskStatic(user_id.tel), globalSearch || phoneSearch)}
            </Typography.Text>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={phoneSearchRef}
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value.replace(/[^0-9]/g, ''))}
                onPressEnter={() => {
                  saveFieldValue({ tel: phoneSearch })
                  close()
                }}
                placeholder='Номер телефона'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue({ tel: phoneSearch })
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setPhoneSearch('')
                    saveFieldValue({ tel: '' })
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => phoneSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: phoneSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: <>Выполнено<br />заказов</>,
        dataIndex: 'completed_orders_count',
        width: '10%',
        onCell: ({ _id }) => {
          return {
            onClick: () => navigate(getSellerEditPath(_id))
          }
        }
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: '20%',
        ...(status === ESellerStatus.APPROVED && {
          filters: disableStateFilters,
          filterMode: 'tree' as const,
          filteredValue: disableStates.length ? disableStates : null
        }),
        // Статус с подсказкой карточку не открывает: на сенсорном экране тап по нему
        // показывает подсказку
        onCell: ({ _id, status }) =>
          status === ESellerStatus.APPROVED
            ? { className: styles.statusCell, onClick: openStatusHint }
            : { onClick: () => navigate(getSellerEditPath(_id)) },
        render: (_, seller) => {
          const { status, _id } = seller

          // Подтвердить отклонённого продавца можно только здесь
          if (status !== ESellerStatus.APPROVED) {
            return (
              <div onClick={(event) => event.stopPropagation()}>
                <StatusDropdown
                  items={getSellerStatusItems(status)}
                  label={sellerStatuses[status].label}
                  color={sellerStatuses[status].color}
                  onClick={(e) => onChangeSellerStatus(e, _id)}
                />
              </div>
            )
          }

          return <SellerDisableStatus seller={seller} />
        }
      }
    ],
    [sellers, moderationSellers, moderationPage, idSearch, fullNameSearch, shopNameSearch, phoneSearch, status, disableStates]
  )

  // На телефоне колонки собраны в две: продавец с контактами и статус.
  // Рендеры берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<ISeller> = React.useMemo(() => {
    const findColumn = (dataIndex: string) =>
      columns.find((column) => (column as any).dataIndex === dataIndex)

    const numberColumn = findColumn('number')
    const nameColumn = findColumn('name')
    const shopColumn = findColumn('shopName')
    const statusColumn = findColumn('status')

    return [
      {
        ...shopColumn,
        title: 'Продавец',
        width: '60%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileShopName}>
              {shopColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {nameColumn?.render?.(record.user_id, record, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              ID {numberColumn?.render?.(record.number, record, index) as React.ReactNode}
            </span>
            {!!record.user_id?.tel && (
              // ссылка, а не текст: тап по номеру звонит, а карточка продавца открывается с остальной ячейки
              <a
                href={`tel:${phoneMaskStatic(record.user_id.tel).replace(/[^\d+]/g, '')}`}
                className={styles.mobilePhone}
                onClick={(event) => event.stopPropagation()}
              >
                {phoneMaskStatic(record.user_id.tel)}
              </a>
            )}
          </div>
        )
      },
      {
        ...statusColumn,
        width: '40%'
      }
    ]
  }, [columns])

  return (
    <div style={{ paddingBottom: 80 }}>
      <MainTitle text='Продавцы' />
      {!isSummaryFailed && <SellersSummary summary={summary} />}
      {moderationTotal > 0 && (
        <section className={styles.moderation}>
          <Typography.Title level={4} className={styles.moderationTitle}>
            На модерации: {moderationTotal}
          </Typography.Title>
          <Table
            scroll={isMobile ? undefined : { x: 950 }}
            size={isMobile ? 'small' : undefined}
            tableLayout={isMobile ? 'fixed' : undefined}
            columns={withoutFilters(isMobile ? mobileColumns : columns)}
            dataSource={moderationSellers}
            loading={isModerationLoading}
            rowKey={(el) => el._id}
            rowClassName='tableRow'
            pagination={{
              current: moderationPage,
              onChange: getModerationSellers,
              pageSize,
              total: moderationTotal,
              hideOnSinglePage: true,
              showSizeChanger: false
            }}
          />
        </section>
      )}
      <div className={styles.header}>
        <Input
          value={globalSearch}
          onChange={(e) => {
            setGlobalSearch(e.target.value)
            debouncedGetSellersByGlobalSearch(e.target.value)
          }}
          placeholder='Поиск (название/имя/телефон)'
          size='large'
          suffix={<SearchOutlined />}
          className={classNames('searchInput', styles.searchInput)}
        />
        {!isSortButton && (
          <Select
            value={statusFirst}
            options={statusFirstOptions}
            onChange={onChangeStatusFirst}
            popupMatchSelectWidth={false}
            size='large'
            className={styles.sortSelect}
          />
        )}
        <div className={styles.headerActions}>
          <Checkbox
            checked={status === ESellerStatus.DECLINED}
            onChange={(e) => onChangeDeclinedOnly(e.target.checked)}
            className={styles.declinedCheckbox}
          >
            Отклонённые
          </Checkbox>
          {isSortButton && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: statusFirstOptions.map(({ label, value }) => ({ label, key: value })),
                selectable: true,
                selectedKeys: [statusFirst],
                onClick: ({ key }) => onChangeStatusFirst(key)
              }}
            >
              <Button size='large' icon={<SwapOutlined rotate={90} />} className={styles.sortButton} />
            </Dropdown>
          )}
          <Button
            size='large'
            onClick={() => setIsReportModalOpen(true)}
            className={styles.addSellerButton}
          >
            <DownloadOutlined /> Скачать отчёт
          </Button>
          <Button
            size='large'
            icon={<DownloadOutlined />}
            onClick={() => setIsReportModalOpen(true)}
            className={styles.reportButtonMobile}
          >
            Отчёт
          </Button>
          <Link to={superAdminRoutes.SELLERS_ADD}>
            <Button type='primary' size='large' className={styles.addSellerButton}>
              <UserAddOutlined /> Добавить продавца
            </Button>
            <Button type='primary' size='large' icon={<PlusOutlined />} className={styles.addSellerButtonMobile} />
          </Link>
        </div>
      </div>
      <Table
        scroll={isMobile ? undefined : { x: 950 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={sellers}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowSelection={{
          selectedRowKeys: selectedItemsId,
          onChange: onRowSelection,
          columnWidth: isMobile ? 32 : undefined
        }}
        rowClassName='tableRow'
        onChange={onTableChange}
        footer={() => (
          <Space size={8}>
            <Typography.Text>Действия:</Typography.Text>
            <Button
              danger
              type='primary'
              icon={<DeleteOutlined />}
              onClick={showDeleteConfirm}
              disabled={!selectedItemsId.length}
            >
              Удалить
            </Button>
          </Space>
        )}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
      <SellersReportModal open={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} />
    </div>
  )
}
